import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Building2, DoorOpen, Layers, LayoutGrid, List, Shapes } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';

import {
    AdminPage,
    ConfirmActionDialog,
    EmptyState,
    KpiCard,
    PillTabs,
    SearchField,
    SectionCard,
    TablePagination,
    formatNumber,
} from '../../../components/design-system/admin';
import { Space } from '../../../modules/vendor/my-spaces/models/space.model';
import { SpacesAsList } from '../../../modules/superadmin/our-spaces/components/spaces-as-list.component';
import { SpacesGrid } from '../../../modules/superadmin/our-spaces/components/spaces-grid.component';
import { SpaceView } from '../../../modules/superadmin/our-spaces/components/space-view.component';
import {
    addedWithin,
    countByRoomType,
    matchesSpaceSearch,
    surfaceLabel,
    uniqueSurfaces,
} from '../../../modules/superadmin/our-spaces/spaces.utils';
import { useSpacesApi } from '../../../common/hooks/use-spaces-api.hook';

type ViewMode = 'grid' | 'list';

const VIEW_KEY = 'ardealy.admin.spaces.view';
const PAGE_SIZES = [12, 24, 48];
const ALL = '__all__';

/** Grid unless this admin last chose the list. */
function initialView(): ViewMode {
    try {
        return localStorage.getItem(VIEW_KEY) === 'list' ? 'list' : 'grid';
    } catch {
        return 'grid';
    }
}

/**
 * The space library: every room photo vendors can pick (visitor uploads are
 * left out by the API). Loaded once; search, room filter and paging happen
 * here and apply to both the grid and the list.
 */
export const OurSpaces = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const spacesAPI = useSpacesApi();

    const [displayedSpaces, setDisplayedSpaces] = useState<Space[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadFailed, setLoadFailed] = useState(false);
    const [filter, setFilter] = useState('');
    const [roomType, setRoomType] = useState<string>(ALL);
    const [viewMode, setViewMode] = useState<ViewMode>(initialView);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);
    const [selectedSpaceIdForView, setSelectedSpaceIdForView] = useState<string | null>(null);
    // The dialog keeps its space after closing so the text doesn't blank out mid-animation.
    const [spaceToDelete, setSpaceToDelete] = useState<Space | null>(null);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [busy, setBusy] = useState(false);

    const fetchSpaces = async () => {
        setLoading(true);
        try {
            const spaces = await spacesAPI.findAllSpaces();
            setDisplayedSpaces(spaces?.data ?? []);
            setLoadFailed(false);
        } catch (error) {
            console.error('Error fetching spaces:', error);
            setLoadFailed(true);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSpaces();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // The Space Manager opens the space named in its URL
    const onOpenInManager = (space: Space) => navigate(`/space-manager?space=${encodeURIComponent(space.id)}`);

    const onDelete = (space: Space) => {
        setSpaceToDelete(space);
        setDeleteOpen(true);
    };

    const handleRemoveSpace = async (space: Space) => {
        setBusy(true);
        try {
            await spacesAPI.findOneSpaceAndDelete(space.id);
            toast.success(t('ADMIN_SPACES.DELETE_DIALOG.DONE', { name: space.name || space.id }));
        } catch (error: any) {
            console.error('Error deleting space:', error);
            const message = error?.message && !error.message.startsWith('Request failed')
                ? error.message
                : t('ADMIN_SPACES.DELETE_DIALOG.FAILED');
            toast.error(message);
        } finally {
            // Always re-fetch — succeed or fail, the list state on screen
            // should match the DB state after the attempt.
            await fetchSpaces();
            setBusy(false);
            setDeleteOpen(false);
        }
    };

    const handleFilterChange = (value: string) => {
        setFilter(value);
        setPage(0);
    };

    const handleRoomTypeChange = (value: string) => {
        setRoomType(value);
        setPage(0);
    };

    const changeViewMode = (mode: ViewMode) => {
        setViewMode(mode);
        try {
            localStorage.setItem(VIEW_KEY, mode);
        } catch {
            // storage blocked: the view just isn't remembered
        }
    };

    // Numbers for the tiles, from the loaded library.
    const roomTypes = useMemo(() => countByRoomType(displayedSpaces), [displayedSpaces]);
    const surfaces = useMemo(() => uniqueSurfaces(displayedSpaces), [displayedSpaces]);
    const recent = useMemo(() => addedWithin(displayedSpaces, 30), [displayedSpaces]);

    // One filtered list feeds both views — the grid used to ignore the search.
    const filteredSpaces = useMemo(
        () =>
            displayedSpaces.filter(
                (space) => (roomType === ALL || space.spaceType === roomType) && matchesSpaceSearch(space, filter),
            ),
        [displayedSpaces, roomType, filter],
    );

    const lastPage = Math.max(0, Math.ceil(filteredSpaces.length / pageSize) - 1);
    // A room every new demo prospect gets (when it suits the product), or no longer.
    const onToggleDemo = async (space: Space) => {
        const value = !space.isDemoDefault;
        try {
            await spacesAPI.setDemoDefault(space.id, value);
            setDisplayedSpaces((prev) => prev.map((s) => (s.id === space.id ? { ...s, isDemoDefault: value } : s)));
            toast.success(t(value ? 'ADMIN_SPACES.DEMO.MARKED' : 'ADMIN_SPACES.DEMO.UNMARKED', { name: space.name || space.id }));
        } catch (error: any) {
            const message = error?.message && !error.message.startsWith('Request failed') ? error.message : t('ADMIN_SPACES.DEMO.FAILED');
            toast.error(message);
        }
    };

    const currentPage = Math.min(page, lastPage);
    const pageSpaces = filteredSpaces.slice(currentPage * pageSize, (currentPage + 1) * pageSize);

    const firstLoad = loading && displayedSpaces.length === 0;
    const viewProps = {
        spaces: pageSpaces,
        onView: (space: Space) => setSelectedSpaceIdForView(space.id),
        onDelete,
        onOpenInManager,
        onToggleDemo,
    };

    const pagination = (
        <TablePagination
            page={currentPage}
            pageSize={pageSize}
            total={filteredSpaces.length}
            onPageChange={setPage}
            onPageSizeChange={(size) => {
                setPageSize(size);
                setPage(0);
            }}
            pageSizes={PAGE_SIZES}
        />
    );

    return (
        <AdminPage
            title={t('ADMIN_SHELL.NAV.SPACES')}
            description={t('ADMIN_SPACES.DESCRIPTION')}
            actions={
                <Button variant="outline" asChild>
                    <Link to="/space-manager">
                        <Layers />
                        {t('ADMIN_SPACES.OPEN_MANAGER_PAGE')}
                    </Link>
                </Button>
            }
        >
            {loadFailed && displayedSpaces.length === 0 ? (
                <EmptyState
                    title={t('ADMIN_SHELL.COMMON.LOAD_FAILED')}
                    action={
                        <Button variant="outline" onClick={fetchSpaces}>
                            {t('ADMIN_SHELL.COMMON.RETRY')}
                        </Button>
                    }
                    className="py-24"
                />
            ) : (
                <>
                    <div
                        className={cn('grid grid-cols-1 gap-4', firstLoad || roomTypes.length ? 'sm:grid-cols-3' : 'sm:grid-cols-2')}
                        data-testid="spaces-kpis"
                    >
                        <KpiCard
                            label={t('ADMIN_SPACES.KPI.TOTAL')}
                            value={formatNumber(displayedSpaces.length)}
                            hint={t('ADMIN_SPACES.KPI.TOTAL_HINT', { count: recent })}
                            icon={<Building2 />}
                            loading={firstLoad}
                            testId="kpi-spaces-total"
                        />
                        {(firstLoad || roomTypes.length > 0) && (
                            <KpiCard
                                label={t('ADMIN_SPACES.KPI.ROOM_TYPES')}
                                value={formatNumber(roomTypes.length)}
                                hint={roomTypes
                                    .slice(0, 3)
                                    .map((r) => `${r.type} ${formatNumber(r.count)}`)
                                    .join(' · ')}
                                icon={<DoorOpen />}
                                loading={firstLoad}
                                testId="kpi-spaces-room-types"
                            />
                        )}
                        <KpiCard
                            label={t('ADMIN_SPACES.KPI.SURFACE_TYPES')}
                            value={formatNumber(surfaces.length)}
                            hint={surfaces.map((s) => surfaceLabel(t, s)).join(' · ')}
                            icon={<Shapes />}
                            loading={firstLoad}
                            testId="kpi-spaces-surfaces"
                        />
                    </div>

                    <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                        <div className="min-w-0">
                            {roomTypes.length > 1 && (
                                <PillTabs<string>
                                    value={roomType}
                                    onChange={handleRoomTypeChange}
                                    ariaLabel={t('ADMIN_SPACES.FILTER_LABEL')}
                                    options={[
                                        { value: ALL, label: t('ADMIN_SHELL.COMMON.ALL'), count: displayedSpaces.length },
                                        ...roomTypes.map((r) => ({ value: r.type, label: r.type, count: r.count })),
                                    ]}
                                />
                            )}
                        </div>
                        <div className="flex items-center gap-2">
                            <SearchField
                                value={filter}
                                onChange={handleFilterChange}
                                placeholder={t('ADMIN_SPACES.SEARCH_PLACEHOLDER')}
                                className="flex-1 sm:w-72 sm:flex-none"
                            />
                            <PillTabs<ViewMode>
                                value={viewMode}
                                onChange={changeViewMode}
                                ariaLabel={t('ADMIN_SPACES.VIEW_LABEL')}
                                className="shrink-0"
                                options={[
                                    { value: 'grid', label: <span className="sr-only sm:not-sr-only">{t('ADMIN_SHELL.COMMON.GRID')}</span>, icon: <LayoutGrid /> },
                                    { value: 'list', label: <span className="sr-only sm:not-sr-only">{t('ADMIN_SHELL.COMMON.LIST')}</span>, icon: <List /> },
                                ]}
                            />
                        </div>
                    </div>

                    {firstLoad ? (
                        viewMode === 'grid' ? (
                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                                {Array.from({ length: 8 }, (_, i) => (
                                    <Skeleton key={i} className="aspect-4/3 rounded-xl" />
                                ))}
                            </div>
                        ) : (
                            <Skeleton className="h-96 rounded-xl" />
                        )
                    ) : filteredSpaces.length === 0 ? (
                        <SectionCard>
                            {displayedSpaces.length === 0 ? (
                                <EmptyState
                                    icon={<Building2 />}
                                    title={t('ADMIN_SPACES.EMPTY.TITLE')}
                                    description={t('ADMIN_SPACES.EMPTY.DESCRIPTION')}
                                    action={
                                        <Button variant="outline" asChild>
                                            <Link to="/space-manager">
                                                <Layers />
                                                {t('ADMIN_SPACES.OPEN_MANAGER_PAGE')}
                                            </Link>
                                        </Button>
                                    }
                                />
                            ) : (
                                <EmptyState
                                    icon={<Building2 />}
                                    title={t('ADMIN_SPACES.NO_MATCH.TITLE')}
                                    description={t('ADMIN_SPACES.NO_MATCH.DESCRIPTION')}
                                    action={
                                        <Button
                                            variant="outline"
                                            onClick={() => {
                                                handleFilterChange('');
                                                handleRoomTypeChange(ALL);
                                            }}
                                        >
                                            {t('ADMIN_SHELL.COMMON.CLEAR')}
                                        </Button>
                                    }
                                />
                            )}
                        </SectionCard>
                    ) : viewMode === 'list' ? (
                        <SectionCard flush className="pt-0" testId="spaces-table">
                            <SpacesAsList {...viewProps} />
                            <div className="border-t">{pagination}</div>
                        </SectionCard>
                    ) : (
                        <div className="flex flex-col gap-2">
                            <SpacesGrid {...viewProps} />
                            {pagination}
                        </div>
                    )}
                </>
            )}

            {selectedSpaceIdForView && (
                <SpaceView
                    selectedSpaceForViewID={selectedSpaceIdForView}
                    setSelectedSpaceForView={setSelectedSpaceIdForView}
                    onOpenInManager={onOpenInManager}
                />
            )}

            <ConfirmActionDialog
                open={deleteOpen}
                onOpenChange={setDeleteOpen}
                title={t('ADMIN_SPACES.DELETE_DIALOG.TITLE')}
                description={
                    spaceToDelete
                        ? t('ADMIN_SPACES.DELETE_DIALOG.DESCRIPTION', { name: spaceToDelete.name || spaceToDelete.id })
                        : undefined
                }
                confirmLabel={t('ADMIN_SHELL.COMMON.DELETE')}
                destructive
                busy={busy}
                onConfirm={() => {
                    if (spaceToDelete) return handleRemoveSpace(spaceToDelete);
                }}
            />
        </AdminPage>
    );
};

export default OurSpaces;
